import { format, parse } from 'url';

/**
 * Removes double slashes (//) from url (but not the one after the protocol)
 * Optionally enforces sensible trailing slashes on returned URL
 * @param  {String}  url                   URL to sanitize
 * @param  {Boolean} trailingSlashRequired append a trailing slash to URL (if not path to a file)
 * @returns {String}                        sanitized URL
 */
export function sanitizeURL(url, trailingSlashRequired = false) {
    let result = _.trim(url);
    const beginsWithProtocol = /^(.*:\/\/)(.*)$/;
    const matches = beginsWithProtocol.exec(result);

    if (matches) {
        result = `${matches[1]}${matches[2].replace(/\/\//g, '/')}`;
    } else {
        result = result.replace(/\/\//g, '/');
    }

    if (trailingSlashRequired) {
        const urlParts = parse(result);
        let urlPath = _.get(urlParts, 'pathname', '');

        // urllib.parse can return a null value for the properties in the resulting object
        if (!urlPath) {
            urlPath = '';
        }

        // Do not append a trailing slash on URL paths that have a filename extension (.js, .html, .css, etc)
        if (urlPath.search(/\.\S+$/) === -1) {
            if (urlPath === '') {
                urlPath = '/';
            } else if (!_.endsWith(urlPath, '/')) {
                urlPath = `${urlPath}/`;
            }

            urlParts.pathname = urlPath;

            result = format(urlParts);
        }
    }

    return result;
}

/**
 * Get a sensible country code for typical site (language) URLs
 * @param  {String} footerNavItemURL the url to be rendered for a footer nav item that takes the user to a different brand site
 * @returns {String}                  a capitalized country code, suitable for country configuration A15 call
 */
export function extractCountryCodeFromFooterSiteURL(footerNavItemURL) {
    // remove whitespace and any surrounding slashes
    let normalizedURL = _.trim(_.trim(footerNavItemURL), '/');
    let countryCode = _.trim(_.last(normalizedURL.split('-')).toUpperCase());

    if (countryCode === 'UK') {
        countryCode = 'GB';
    }

    if (countryCode === '') {
        countryCode = 'US';
    }

    return countryCode;
}

export function getSlugFromURL(url, contextPath) {
    const urlParts = parse(url);
    const pathFragments = _.reject(_.split(urlParts.pathname, '/'), (fragment) => _.trim(fragment) === '');
    const normalizedContextPath = _.trim(contextPath, '/');
    const nonCategoryPaths = ['shop', 'help'];
    const firstNonCategoryPath = _.head(_.intersection(pathFragments, nonCategoryPaths));

    if (firstNonCategoryPath) {
        const fragmentsStartingWithShop = _.dropWhile(pathFragments, (fragment) => fragment !== firstNonCategoryPath);

        if (fragmentsStartingWithShop.length > 1) {
            return fragmentsStartingWithShop[1];
        }
    }

    if (pathFragments.length > 1 && pathFragments[0] === normalizedContextPath) {
        return pathFragments[1];
    }

    return pathFragments[0];
}

export function buildUrl(state, path, stripParams = false) {
    const canonicalUrl = _.get(state, 'core.constants.urls.canonical', '').trim();
    const contextPath = _.get(state, 'core.contextPath', '/');

    // combine and strip double slashes, except the slashes after http: https:
    let fullUrl = `${canonicalUrl}${contextPath}${path}`.replace(/([^:]\/)\/+/g, '$1');

    if (stripParams) {
        fullUrl = fullUrl.split('?')[0];
    }

    return fullUrl;
}
